/**
 * The words of the Hiring dashboard (Dashboard › Hiring), the Stores page's small Hiring
 * column and "Link an opening" (approved mock-up of 15 Oct 2026, panels 1, 5 and 6).
 *
 * Short on purpose: the screens carry a label, a number and a word, and anything longer
 * sits on hover. Every word those three screens print is here, with the small rules that
 * pick it, so a test can hold the words without drawing a page.
 *
 * Nothing here ever names a candidate: the dashboard, the column and the Link window show
 * counts and openings only. The one exception is the dashboard's Download (28 Oct 2026), whose
 * words are at the end: its file names every candidate Recruitment lists, for everyone who may
 * see hiring (the product owner), and is written in the browser (hiringExport.ts). A search
 * typed on the dashboard is never put in an address, a file name, a log line or an analytics
 * event.
 */
import type {
  HiringDashStatus, HiringLink, HiringPickerOpening, HiringRequest, HiringStageCounts,
} from '../../services/api';
import {
  DELETED_WORDS, OPENING_ROW_WORDS, OPENING_WORDS, RAISE_WORDS, deletedInRecruitment, openingAddress, openingNames, openingSearchText,
  type PickState,
} from './hiringText';

/**
 * What the words of a row's status are worked out from: the fields of a dashboard row that the
 * Download's openings carry too, under the same names (they come without ChannelPlex's ids).
 */
export type DashStatusFacts = Pick<HiringLink, 'dashStatus' | 'state' | 'attention' | 'needsAttention' | 'status' | 'lastKnownStatus' | 'numbers'>;

/* ─────────────────────────────── the four statuses ─────────────────────────────── */

/** The four statuses, in the order the dashboard's filter shows them. */
export const DASH_STATUSES: readonly HiringDashStatus[] = ['being_raised', 'hiring', 'filled', 'needs_attention'];

export const DASH_STATUS_WORDS: Record<HiringDashStatus, string> = {
  being_raised: 'Being raised',
  hiring: 'Hiring',
  filled: 'Filled',
  needs_attention: 'Needs attention',
};

/** The dot beside each word: grey, blue, green, amber (the mock-up's four). */
export const DASH_STATUS_DOT: Record<HiringDashStatus, string> = {
  being_raised: 'bg-gray-400',
  hiring: 'bg-blue-500',
  filled: 'bg-green-600',
  needs_attention: 'bg-amber-600',
};

/**
 * One link's status word. The server sends it on every link (`dashStatus`); a link from a
 * server that does not is worked out the server's way (hiring-link.logic.ts dashStatusOf).
 * Null for a link nobody follows any more: it is none of the four.
 */
export const dashStatusOfLink = (link: DashStatusFacts): HiringDashStatus | null => {
  if (link.dashStatus) return link.dashStatus === 'closed' ? null : link.dashStatus;
  if (link.state === 'sending') return 'being_raised';
  if (link.state === 'closed') return null;
  // Not `team_unconfirmed` alone: a just-raised opening's people not read back yet needs nobody (the server's rule).
  if ((link.attention ?? []).some((reason) => reason !== 'team_unconfirmed') || link.needsAttention === true) return 'needs_attention';
  if (link.status === 'not_listed' || link.status === 'closed_in_recruitment') return 'needs_attention';
  if (link.status === 'numbers_unavailable' && link.lastKnownStatus === 'not_listed') return 'needs_attention';
  const n = link.numbers;
  if (!n) return 'hiring';
  return n.open > 0 || n.offered > 0 ? 'hiring' : 'filled';
};

/**
 * Why a link needs attention, in a few words, by the server's reason. Shown on hover.
 * `deleted` and `deleted_when_last_checked` are not the server's reasons: they are the status
 * `not_listed` (a good read of the budget's list did not find the opening, and 1Office leaves
 * out only a deleted one), now or as the last good read found it.
 */
const ATTENTION_HOVER: Record<string, string> = {
  deleted: DELETED_WORDS.badge,
  // Recruitment cannot be read just now, and its last read found the opening deleted.
  deleted_when_last_checked: DELETED_WORDS.whenLastChecked,
  // An opening ChannelPlex raised that the list has not shown yet: Recruitment's read copy may be behind.
  not_listed: 'Not in Recruitment’s list',
  address_changed: 'Address changed in Recruitment',
  positions_changed: 'Positions changed in Recruitment',
  closed_in_recruitment: 'Cancelled in Recruitment',
  send_unknown: 'Recruitment did not confirm it',
  store_changed: 'The store changed',
  moved_in_channelplex: 'Moved in ChannelPlex',
  // One reason, one wording: the opening's own pill says it in these words too.
  created_incomplete: RAISE_WORDS.incompleteTitle,
  team_unconfirmed: 'Team not confirmed yet',
  cancel_open: 'Cancel not confirmed yet',
};
const ATTENTION_FALLBACK = 'Someone needs to look at it';

/** The hover of "Needs attention": each reason once, in the server's order. Null for any other status. */
export const attentionHover = (link: DashStatusFacts): string | null => {
  if (dashStatusOfLink(link) !== 'needs_attention') return null;
  const reasons = [...(link.attention ?? [])];
  // Said once: a raised opening's own "not in the list" question already says it.
  const gone = link.status === 'not_listed' || (link.status === 'numbers_unavailable' && link.lastKnownStatus === 'not_listed');
  if (gone && !reasons.includes('not_listed')) reasons.push(link.status === 'not_listed' ? 'deleted' : 'deleted_when_last_checked');
  if (link.status === 'closed_in_recruitment') reasons.push('closed_in_recruitment');
  const words = [...new Set(reasons.map((r) => ATTENTION_HOVER[r] ?? ATTENTION_FALLBACK))];
  return words.length > 0 ? words.join(' · ') : ATTENTION_FALLBACK;
};

/**
 * A row's status word on the dashboard: one of the four, except an opening deleted in
 * Recruitment, which says so (21 Oct 2026) - in the dot of Needs attention, and under that
 * filter, until someone stops following it. Null for a link nobody follows any more.
 */
export const dashWordOfLink = (link: DashStatusFacts): string | null => {
  const status = dashStatusOfLink(link);
  if (!status) return null;
  return status === 'needs_attention' && deletedInRecruitment(link) ? DELETED_WORDS.badge : DASH_STATUS_WORDS[status];
};

/* ─────────────────────────────── a row of the dashboard ─────────────────────────────── */

/** "Gadget Hub Indiranagar (GHI-03)"; null for a hiring with no store ("No store", in grey). */
export const storeWords = (link: Pick<HiringLink, 'subject'>): string | null => {
  if (link.subject.kind !== 'store') return null;
  const name = (link.subject.storeName ?? '').trim();
  const code = (link.subject.storeCode ?? '').trim();
  if (name && code) return `${name} (${code})`;
  return name || code || 'A store';
};

/**
 * What the opening is for: the budget's designation. A hiring with no store raised or linked
 * since 15 Oct 2026 is named after its budget; one linked before keeps the name typed for it.
 */
export const designationOf = (link: {
  subject: { kind: 'store' } | { kind: 'role'; roleLabel: string | null };
  budget?: { name: string } | null;
}): string => {
  const typed = link.subject.kind === 'role' ? (link.subject.roleLabel ?? '').trim() : '';
  return typed || (link.budget?.name ?? '').trim() || 'Opening';
};

/**
 * Where a row's opening is, for its Address, City and State (26 Oct 2026, the product owner: "so
 * that we can know which respective address we are hiring the candidates for"): as Recruitment
 * has the opening now, else as it was when it was linked - for a store's opening and one with no
 * store alike. Each part null when not known (its cell reads "–"); the State is named by the
 * server from Recruitment's city list, and is null until that list has been read.
 */
export const dashPlaceOf = (link: Pick<HiringLink, 'address'>): { address: string | null; city: string | null; state: string | null } => {
  const side = link.address?.now ?? link.address?.linked ?? null;
  const text = (value: string | null | undefined): string | null => String(value ?? '').trim().replace(/\s+/g, ' ') || null;
  return { address: text(side?.location), city: text(side?.cityName), state: text(side?.stateName) };
};

/** Every candidate the last read counted on the opening (every stage, and the ones it could not place). Null when none was read. */
export const candidatesTotal = (stages: HiringStageCounts | null | undefined): number | null => {
  if (!stages) return null;
  const all = stages.screening + stages.interview + stages.selected + stages.offered
    + stages.joined + stages.dropped + stages.leftAfterJoining + stages.other;
  return Number.isFinite(all) ? all : null;
};

/** A number cell, or a dash where nothing was read. */
export const numberOrDash = (n: number | null | undefined): string => (typeof n === 'number' && Number.isFinite(n) ? String(n) : '–');

const INDIA = 'Asia/Kolkata';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayKey = (d: Date): string => d.toLocaleDateString('en-CA', { timeZone: INDIA });

/**
 * "Updated": "Today 10:42", "Yesterday", "13 Oct", or "13 Oct 2025" from another year. India time.
 * A dash when the server did not say.
 */
export const updatedWords = (iso: string | null | undefined, now: Date = new Date()): string => {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  const today = dayKey(now);
  const yesterday = dayKey(new Date(now.getTime() - 24 * 60 * 60 * 1000));
  const day = dayKey(d);
  if (day === today) {
    const time = d.toLocaleTimeString('en-GB', { timeZone: INDIA, hour: '2-digit', minute: '2-digit', hour12: false });
    return `Today ${time}`;
  }
  if (day === yesterday) return 'Yesterday';
  // From the India-time day itself ("2031-10-13"), with three-letter months: some browsers write "Sept".
  const [year, month, date] = day.split('-');
  const short = `${date} ${MONTHS[Number(month) - 1] ?? month}`;
  return year === today.slice(0, 4) ? short : `${short} ${year}`;
};

/** The pages a pager shows: the first, the last, the one open and those beside it, with gaps as null. */
export const pagerItems = (current: number, last: number): Array<number | null> => {
  if (last <= 1) return [1];
  const wanted = new Set<number>([1, last, current - 1, current, current + 1]);
  // At either end, three in a row: "1 2 3 … 8", never "1 2 … 8".
  if (current <= 2) wanted.add(3);
  if (current >= last - 1) wanted.add(last - 2);
  const pages = [...wanted].filter((p) => p >= 1 && p <= last).sort((a, b) => a - b);
  const out: Array<number | null> = [];
  pages.forEach((p, i) => {
    if (i > 0 && p - pages[i - 1] > 1) out.push(null);
    out.push(p);
  });
  return out;
};

/* ─────────────────────────────── the dashboard ─────────────────────────────── */

/** Under a status with nothing in it: one plain sentence each. */
const NONE_OF_STATUS: Record<HiringDashStatus, string> = {
  being_raised: 'No hiring is being raised.',
  hiring: 'No opening is hiring.',
  filled: 'No opening is filled.',
  needs_attention: 'No opening needs attention.',
};

export const DASHBOARD_WORDS = {
  /** The server looks in the store, the opening, its budget, its address, its city and its State (26 Oct 2026). */
  searchLabel: 'Search store, opening, city or state',
  searchPlaceholder: 'Search store, opening, city or state',
  filterLabel: 'Show',
  all: 'All',
  raise: 'Raise hiring',
  ask: 'Ask for hiring',
  /**
   * The hover of "Open Manage Budgets" beside the title: that the openings can be raised and
   * edited on 1Office's page itself (the product owner, 15 Oct 2026). An opening's own
   * "Open in Manage Budgets" keeps BUDGETS_PAGE_WORDS.hover.
   */
  /** The dashboard's Manage Budgets button: it says what can be done there (product owner, 15 Oct 2026). */
  budgetsButton: 'Raise in Manage Budgets',
  budgetsHover: "Opens 1Office's Manage Budgets in a new tab, where you can raise and edit openings directly.",
  columns: {
    store: 'Store', opening: 'Opening', address: 'Address', city: 'City', state: 'State',
    status: 'Status', open: 'Open', candidates: 'Candidates', updated: 'Updated',
  },
  noStore: 'No store',
  /** The one-line strip above the table, only while hiring requests wait. */
  waiting: (count: number): string => (count === 1 ? '1 request waiting' : `${count} requests waiting`),
  review: 'Review',
  /** A row's name for a screen reader: whose opening it opens. */
  openRow: (link: HiringLink): string => {
    const where = storeWords(link) ?? DASHBOARD_WORDS.noStore;
    const what = designationOf(link);
    return link.openingId != null ? `Open ${what}, opening ${link.openingId}, ${where}` : `Open ${what}, ${where}`;
  },
  /** Under the table: "1–12 of 86". */
  shown: (from: number, to: number, total: number): string => (total === 0 ? '0 of 0' : `${from}–${to} of ${total}`),
  pager: 'Pages',
  previous: 'Previous page',
  next: 'Next page',
  page: (n: number): string => `Page ${n}`,
  none: 'No openings are followed yet.',
  noneFound: 'Nothing matches.',
  noneOfStatus: (status: HiringDashStatus): string => NONE_OF_STATUS[status],
  loadFailed: 'Could not load hiring.',
  tryAgain: 'Try again',
  /** Recruitment did not answer for some or all of the openings on the page: their numbers are the last read's. */
  readPartial: 'Some numbers are from the last read: Recruitment did not answer for every budget.',
  readNone: 'Numbers are from the last read: Recruitment could not be read just now.',
} as const;

/* ─────────────────────────────── the waiting requests ("Review") ─────────────────────────────── */

export const DASHBOARD_REQUEST_WORDS = {
  back: 'Back to openings',
  heading: 'Hiring requests',
  waitingTab: (count: number): string => `Waiting (${count})`,
  pastTab: 'Past',
  columns: { for: 'Hiring for', budget: 'Budget and reason', stands: 'Where it stands', askedBy: 'Asked by', actions: 'Actions' },
  noneWaiting: 'No hiring request is waiting.',
  nonePast: 'No past hiring requests.',
  more: 'Show more',
  loading: 'Loading…',
  /** Under a request's name: the store's code, or "No store". */
  subjectLine: (request: Pick<HiringRequest, 'subject'>): string =>
    (request.subject.kind === 'store' ? request.subject.storeCode || 'Store' : DASHBOARD_WORDS.noStore),
} as const;

/* ─────────────────────────────── the Stores page's Hiring column ─────────────────────────────── */

/**
 * A store's one word in the Stores page's Hiring column, from its live links and the hiring
 * request waiting for it (if this person is shown requests). The most pressing wins:
 * Needs attention, then Being raised (a request waiting or a raise being sent), then
 * Hiring, then Filled. Null: nothing is followed or waiting for the store.
 */
const PRESSING: readonly HiringDashStatus[] = ['needs_attention', 'being_raised', 'hiring', 'filled'];
export const storeHiringStatus = (
  links: readonly HiringLink[],
  waitingRequest: HiringRequest | null | undefined = null,
): HiringDashStatus | null => {
  const found = new Set<HiringDashStatus>();
  for (const link of links) {
    const status = dashStatusOfLink(link);
    if (status) found.add(status);
  }
  if (waitingRequest && waitingRequest.standing === 'waiting') found.add('being_raised');
  return PRESSING.find((s) => found.has(s)) ?? null;
};

export const STORE_COLUMN_WORDS = {
  header: 'Hiring',
  /** What the dot and word say to a screen reader, as a button that opens the store's hiring. */
  openFor: (storeName: string, status: HiringDashStatus | null): string =>
    `Hiring for ${storeName}: ${status ? DASH_STATUS_WORDS[status] : 'none'}`,
  /** The hover of a store with a hiring request waiting. */
  waitingHover: 'A hiring request is waiting for confirmation',
} as const;

/**
 * Why a store reads its word, said on hover wherever the word is drawn (the Hiring column of
 * the Stores page and of Dashboard › Deployment › Undeployed Stores, and "Open hiring" on a
 * store's row): the reasons of the opening that needs attention, or a hiring request waiting
 * when nothing of the store is being raised. Null when the word says it all.
 */
export const storeHiringWhy = (
  links: readonly HiringLink[],
  waitingRequest: HiringRequest | null | undefined,
  status: HiringDashStatus | null,
): string | null => {
  if (status === 'needs_attention') {
    const pressing = links.find((l) => dashStatusOfLink(l) === 'needs_attention');
    return pressing ? attentionHover(pressing) : null;
  }
  if (status === 'being_raised' && waitingRequest && !links.some((l) => dashStatusOfLink(l) === 'being_raised')) {
    return STORE_COLUMN_WORDS.waitingHover;
  }
  return null;
};

/* ─────────────────────────────── Link an opening ─────────────────────────────── */

export const LINK_WORDS = {
  title: 'Link an opening',
  info: 'Nothing is sent to Recruitment.',
  store: 'Store',
  optional: 'optional',
  noStore: 'No store',
  storeSearch: 'Search stores',
  /** Under the store list: how many there are (every one is in the list, read as it is scrolled). */
  storesShown: (total: number): string => `${total.toLocaleString('en-IN')} ${total === 1 ? 'store' : 'stores'}`,
  storesMore: 'Loading more…',
  storesNone: 'No store matches.',
  storesFailed: 'Could not load the stores. Try again.',
  storesLoading: 'Loading stores…',
  budget: 'Budget',
  budgetPlaceholder: 'Choose a budget',
  budgetsLoading: 'Loading budgets…',
  budgetsFailed: 'Could not load the budgets. Try again.',
  noBudgets: 'This project has no active budgets in Recruitment.',
  opening: 'Opening',
  chooseBudgetFirst: 'Choose a budget to see its openings.',
  openingsLoading: 'Reading openings…',
  openingsFailed: 'Could not load the openings. Try again.',
  noOpenings: 'This budget has no openings in Recruitment.',
  searchOpenings: 'Search openings',
  searchOpeningsPlaceholder: 'Search address, city, state or people',
  noOpeningMatches: 'No opening matches.',
  /** The table's rows, one per opening, as one choice (a radio group) - its name for a screen reader. */
  openingsGroup: 'Openings',
  /** The table's columns (approved option A, 16 Oct 2026); the first, the radios', is named for a screen reader only. */
  columns: {
    choose: 'Choose',
    address: 'Address',
    city: 'City',
    state: 'State',
    open: 'Open',
    recruiters: 'Recruiters',
    offerMakers: 'Offer makers',
    interviewers: 'Interviewers',
  },
  /**
   * Under the table: every opening is drawn and the table scrolls, so this says how many there
   * are ("N matches" while searching), that open ones come first, and - when a greyed row is on
   * show - that greyed ones cannot be linked, with the reason on their hover.
   */
  openingsShown: (total: number, searching: boolean, greyed: boolean): string => [
    searching
      ? `${total.toLocaleString('en-IN')} ${total === 1 ? 'match' : 'matches'}`
      : `${total.toLocaleString('en-IN')} ${total === 1 ? 'opening' : 'openings'}`,
    'open ones first',
    ...(greyed ? ["greyed ones can't be linked (reason on hover)"] : []),
  ].join(' · '),
  /** Footer left, once an opening is picked: "Chosen:", then its address (bold), then its team (linkChosenTeam). */
  chosen: 'Chosen:',
  cancel: 'Cancel',
  link: 'Link',
  /** The button's name for a screen reader: it starts with the word on it. */
  linkName: (storeName: string | null): string => (storeName ? 'Link to this store' : 'Link with no store'),
  failed: 'Could not link this opening. Try again.',
  tryAgain: 'Try again',
} as const;

/** An opening's short tag in the Link window, from why it can or cannot be picked. The full sentence is on hover. */
export const openingTagWord = (pick: Pick<PickState, 'pickable' | 'shared' | 'tag' | 'noOpenPosition'>): string | null => {
  if (!pick.tag) return null;
  if (pick.pickable) return pick.shared ? 'Shared' : null;
  if (pick.noOpenPosition) return 'None open';
  if (/being raised/i.test(pick.tag)) return 'Being raised';
  if (/several positions/i.test(pick.tag)) return 'Stores only';
  if (/linked/i.test(pick.tag)) return 'Linked';
  return 'Not open';
};

/**
 * Does an opening match what was typed in the Link window: its address, city or State, or the
 * name of anyone on its team (recruiters, offer makers, interviewers, a client named on a
 * round). Its number is not a column, so only the whole number finds it (as a Raise review
 * or 1Office names it, "48213" or "Opening 48213") - never a part of one.
 */
export const linkOpeningMatches = (
  opening: Pick<HiringPickerOpening, 'openingId' | 'location' | 'cityName' | 'stateName' | 'team'>,
  typed: string,
): boolean => {
  const q = typed.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!q) return true;
  if (q === String(opening.openingId) || q === `opening ${opening.openingId}`) return true;
  return openingSearchText(opening).includes(q);
};

/**
 * Where an opening's row stands in the Link window: openings that can be linked with a
 * position open first, then those that can be linked with none open (an offer is out), then
 * those that cannot be linked (greyed). Recruitment's own order within each.
 */
export const linkOpeningRank = (opening: Pick<HiringPickerOpening, 'open'>, pick: Pick<PickState, 'pickable'>): number =>
  (pick.pickable ? (opening.open > 0 ? 0 : 1) : 2);

const chosenRole = (one: string, many: string, people: ReadonlyArray<{ name: string }> | null | undefined): string => {
  const names = openingNames(people);
  return `${names.length === 1 ? one : many} ${names.join(', ') || OPENING_ROW_WORDS.nobody}`;
};

/**
 * The picked opening's team in the footer's Chosen line: "Recruiter A · Offer maker C", or
 * "Recruiters A, B · Offer makers –". Openings at one address differ only in their team, so the
 * address alone could be any of them.
 */
export const linkChosenTeam = (opening: Pick<HiringPickerOpening, 'team'>): string => [
  chosenRole('Recruiter', 'Recruiters', opening.team?.recruiters),
  chosenRole('Offer maker', 'Offer makers', opening.team?.offerMakers),
].join(' · ');

/** The Chosen line whole, on its hover: the opening's number, its address and its team. */
export const linkChosenHover = (opening: Pick<HiringPickerOpening, 'openingId' | 'location' | 'team'>): string =>
  `Opening ${opening.openingId} · ${openingAddress(opening)} · ${linkChosenTeam(opening)}`;

/* ─────────────────────────────── the Download (28 Oct 2026) ─────────────────────────────── */

/** The most openings one file holds: the server's own cap (hiring-link.service.ts EXPORT_MAX). */
export const DOWNLOAD_MAX_OPENINGS = 500;

/**
 * The words of the dashboard's Download and of the file it saves (hiringExport.ts). The file's
 * columns are the dashboard's own column words and the opening panel's (details, numbers and
 * team), so a column says what the screen it comes from says; the few the screens have no word
 * for are here.
 */
export const DOWNLOAD_WORDS = {
  button: 'Download',
  hover: 'One Excel file: every opening on this tab (all pages) with its details and team, and every candidate with their stage.',
  preparing: 'Preparing…',
  /** Anything but a 429 or a 403: no answer, a broken answer, the server failing, or the file not written. */
  failed: 'Could not prepare the download. Please try again.',
  /** The server's sentence for a 429 (hiring-opening.logic.ts OPENING_MESSAGES.exportBusy), for an answer that carries none. */
  busy: 'Another download is being prepared. Try again in a minute.',
  /**
   * The amber line once the file is saved, when there is something to say: "Saved.", then the
   * openings that could not be read (marked in the file, never shown as 0), then those left out.
   */
  saved: 'Saved.',
  savedUnread: (count: number): string => (count === 1
    ? 'Recruitment could not be read for 1 opening; it is marked in the file.'
    : `Recruitment could not be read for ${count} openings; they are marked in the file.`),
  savedCapped: `More than ${DOWNLOAD_MAX_OPENINGS} openings matched: only the newest ${DOWNLOAD_MAX_OPENINGS} are in the file.`,
  sheets: { openings: 'Openings', candidates: 'Candidates', about: 'About' },
  /** The Openings sheet, in this order. */
  openingColumns: [
    DASHBOARD_WORDS.columns.store,
    'Store code',
    DASHBOARD_WORDS.columns.opening,
    'Opening ID',
    DASHBOARD_WORDS.columns.address,
    DASHBOARD_WORDS.columns.city,
    DASHBOARD_WORDS.columns.state,
    OPENING_WORDS.details.pin,
    DASHBOARD_WORDS.columns.status,
    'Needs attention because',
    'Reason',
    OPENING_WORDS.numbers.positions,
    OPENING_WORDS.numbers.filled,
    OPENING_WORDS.numbers.offered,
    OPENING_WORDS.numbers.open,
    DASHBOARD_WORDS.columns.candidates,
    OPENING_WORDS.details.published,
    OPENING_WORDS.details.created,
    OPENING_WORDS.team.recruiters,
    OPENING_WORDS.team.offerMakers,
    'Rounds',
    DASHBOARD_WORDS.columns.updated,
    'Read',
  ],
  /** The Candidates sheet, in this order: the opening's four columns repeated on every row, so a filter needs no lookup. */
  candidateColumns: [
    DASHBOARD_WORDS.columns.store,
    'Store code',
    DASHBOARD_WORDS.columns.opening,
    'Opening ID',
    OPENING_WORDS.columns.name,
    OPENING_WORDS.columns.mobile,
    OPENING_WORDS.columns.source,
    OPENING_WORDS.columns.stage,
    OPENING_WORDS.columns.round,
    OPENING_WORDS.columns.applied,
  ],
  /** The About sheet: a label and its value on each row. */
  aboutColumns: ['Item', 'Value'],
  about: {
    downloadedAt: 'Downloaded at (IST)',
    tab: 'Tab',
    search: 'Search',
    noSearch: 'None',
    openings: 'Openings count',
    candidates: 'Candidates count',
    capped: 'Openings left out',
    cappedNote: `More than ${DOWNLOAD_MAX_OPENINGS} openings matched: only the newest ${DOWNLOAD_MAX_OPENINGS} are in this file.`,
    note: (n: number): string => `Note ${n}`,
  },
  /** The four fixed notes of the About sheet. */
  notes: [
    'Mobile numbers are personal data; do not forward the file.',
    'Recruitment does not list applications still at "Assigned" that name a source (1Office\'s own rule), so "all applications" means all that Recruitment lists.',
    'Round is the round Recruitment\'s status names.',
    '"Last read" figures are from the last good read.',
  ],
  /** The Read column: whether the opening's figures were read from Recruitment for this file, and if not, why. */
  read: {
    now: 'Recruitment now',
    lastRead: (when: string): string => `Last read ${when}`,
    /** The figures are the last good read's, and there never was one. */
    neverRead: 'Never read',
    beingRaised: 'Not read: being raised',
    noIdentity: 'Not read: no 1Office sign-in',
    deleted: DELETED_WORDS.badge,
    /** Closed by Recruitment itself: the screen's own word for it (the Status column's hover). */
    cancelled: ATTENTION_HOVER.closed_in_recruitment,
  },
  /** The Candidates cell of an opening whose candidates could not be read: never 0. */
  candidatesUnread: 'Could not be read',
  /** Why the opening is wanted (the dashboard's reasons). A hand link saved with no reason says nothing. */
  reasons: { replacement: 'Replacement', newStore: 'New store', extraHeadcount: 'Extra headcount' },
  replacementOf: (empCode: string): string => `Replacement of ${empCode}`,
  /** The Created column: "Raised 02 Mar 2031 · Sample Name (910002)". */
  created: { raised: 'Raised', linked: 'Linked' },
  /** A candidate who joined on this opening and has since left it. */
  joinedLeft: 'Joined (left)',
} as const;
